import type {ReactElement} from 'react';
import {useId} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {Text, TextSize} from '@Components/text/text';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {accessibleAssetName, selectedValues, toggleValue} from '@Components/ai-chat-window/components/survey/fields/fields.library';
import {otherKey} from '@Components/ai-chat-window/components/survey/survey.library';
import {SurveyOptionImage} from '@Components/ai-chat-window/components/survey-option-image/survey-option-image';
import type {SurveyOption} from '@Components/ai-chat-window/components/survey/survey.types';
import type {AssetGridFieldRendererProps} from './fields.types';
import styles from './fields.module.scss';

function renderRowThumbnail(option: SurveyOption): ReactElement {
  const renderImage = (): ReactElement | null => {
    if (option.imageUrl === undefined || option.imageUrl === '') {
      return null;
    }

    return <SurveyOptionImage imageUrl={option.imageUrl} className={styles.assetRowImage} alt="" />;
  };

  const renderVideoMark = (): ReactElement | null => {
    if (option.isVideo !== true) {
      return null;
    }

    return (
      <span className={styles.assetRowVideoMark}>
        <Icon icon="icon-play-video" size={IconSize.SIZE_ICON_12} className={styles.assetRowVideoIcon} ariaHidden />
      </span>
    );
  };

  return (
    <span className={styles.assetRowThumbnail} aria-hidden="true">
      {renderImage()}
      {renderVideoMark()}
    </span>
  );
}

function renderSelectionMark(isSingleSelect: boolean, isChecked: boolean): ReactElement {
  if (isSingleSelect) {
    return <span className={isChecked ? `${styles.radio} ${styles.radioSelected}` : styles.radio} aria-hidden="true" />;
  }

  return (
    <span className={isChecked ? `${styles.assetRowCheckbox} ${styles.assetRowCheckboxChecked}` : styles.assetRowCheckbox} aria-hidden="true">
      <Icon icon="icon-check" size={IconSize.SIZE_ICON_12} className={styles.assetRowCheckmark} ariaHidden />
    </span>
  );
}

function renderDescription(option: SurveyOption): ReactElement | null {
  if (option.description === undefined || option.description === '') {
    return null;
  }

  return <Text val={option.description} size={TextSize.MD} className={styles.assetRowDetail} />;
}

function renderSelectionCount(selectionCountId: string, selectedCount: number, maxSelection: number | undefined): ReactElement | null {
  if (maxSelection === undefined) {
    return null;
  }

  return (
    <Text
      val={window.i18next.t('pmwjs_ai_chat_survey_n_of_max_selected', {selected: selectedCount, max: maxSelection})}
      size={TextSize.SM}
      className={styles.assetSelectionCount}
      attributes={{id: selectionCountId, 'aria-live': 'polite'}}
    />
  );
}

export function AssetGridFieldRenderer({name, step, field}: AssetGridFieldRendererProps): ReactElement {
  const {control, setValue} = useFormContext();
  const selectionCountId = useId();
  const isSingleSelect = field.maxSelection === 1;

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={[]}
      render={({field: rhfField}): ReactElement => {
        const selected = selectedValues(rhfField.value);
        const isAtMaxSelection = !isSingleSelect && field.maxSelection !== undefined && selected.length >= field.maxSelection;

        const selectAsset = (option: SurveyOption): void => {
          setValue(otherKey(name), '');
          rhfField.onChange(isSingleSelect ? [option.value] : toggleValue(selected, option.value));
        };

        const renderRow = (option: SurveyOption): ReactElement => {
          const isChecked = selected.includes(option.value);
          const isBlockedByMaxSelection = isAtMaxSelection && !isChecked;

          return (
            <button
              key={option.value}
              type="button"
              role={isSingleSelect ? 'radio' : 'checkbox'}
              aria-checked={isChecked}
              aria-label={accessibleAssetName(option)}
              aria-disabled={isBlockedByMaxSelection}
              className={isChecked ? `${styles.assetRow} ${styles.assetRowSelected}` : styles.assetRow}
              onClick={(): void => {
                if (isBlockedByMaxSelection) {
                  return;
                }

                selectAsset(option);
              }}
            >
              {renderSelectionMark(isSingleSelect, isChecked)}
              {renderRowThumbnail(option)}
              <span className={styles.assetRowText}>
                <Text val={option.label} size={TextSize.MD} bold={isChecked} className={styles.assetRowLabel} />
                {renderDescription(option)}
              </span>
            </button>
          );
        };

        return (
          <div className={styles.assetGridField}>
            <div
              className={styles.assetRowList}
              role={isSingleSelect ? 'radiogroup' : 'group'}
              aria-label={step.helpText}
              aria-describedby={field.maxSelection === undefined ? undefined : selectionCountId}
            >
              {field.options.map(renderRow)}
            </div>
            {renderSelectionCount(selectionCountId, selected.length, field.maxSelection)}
          </div>
        );
      }}
    />
  );
}
